import React, { useState } from "react";
import styles from "./modalWindow.module.scss";
import { AnimatePresence, motion } from "framer-motion";
import Button, { ButtonProps } from "../../atom/Button/Button";

interface ModalWindowProps {
  onComfirm?: () => void;
  onCancel?: () => void;
  title?: string;
  description?: string;
  children?: React.ReactNode;
  titleRender?: React.ReactNode;
  isOpen: boolean;
  setIsOpen: (value: boolean) => void;
  confirmLabel?: string;
  cancelLabel?: string;
  contentRender?: (params: { closeModal: () => void }) => JSX.Element;
  noBtn?: boolean;
  minHeight?: string | number;
}

/**
 *
 * @returns
 */
export default function ModalWindow({
  children,
  onComfirm,
  title,
  titleRender,
  isOpen,
  setIsOpen,
  description,
  confirmLabel = "OK",
  cancelLabel = "Annuler",
  contentRender,
  noBtn,
  minHeight
}: ModalWindowProps) {
  const content = (
    <AnimatePresence mode="wait">
      {isOpen && (
        <motion.div className={styles["global-container"]}>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2, type: "easeInOut" }}
            className={styles["background"]}
        
          />
          <motion.div
            initial={{ opacity: 0, scale: 1.1 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 1.1 }}
            transition={{ duration: 0.2, type: "easeInOut" }}
            className={styles["modal-container"]}
                style={{minHeight: minHeight}}
          >
            {contentRender ? (
              contentRender({ closeModal: () => setIsOpen(false) })
            ) : (
              <>
                <div
                  className={[
                    styles["modal-container__header"],
                    styles["header"],
                  ].join(" ")}
                >
                  {(title || titleRender) && (
                    <div className={styles["header__title"]}>
                      {title} {titleRender}
                    </div>
                  )}
                </div>
                <div className={styles["body"]}>
                  <p className={styles["body__description"]}>{description}</p>
                  {children}
                </div>
                {!noBtn && (
                  <div className={styles["btn-container"]}>
                    {onComfirm && (
                      <div className={styles["btn-container__btn"]}>
                        <Button
                          onClick={onComfirm}
                          label={confirmLabel}
                          height="30px"
                        />
                      </div>
                    )}
                    <div className={styles["btn-container__btn"]}>
                      <Button
                        onClick={() => setIsOpen(false)}
                        label={cancelLabel}
                        height="30px"
                        variant={"gray"}
                      />
                    </div>
                  </div>
                )}
              </>
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
  return <>{content}</>;
}

export type ButtonWithComfirmationModalProps = ButtonProps & {
  titleModal?: string;
  desc?: string;
  onCancel?: () => void;
};

export interface WithModalConfirmationProps {
  buttonRender: ({ openModal }: { openModal: () => void }) => JSX.Element;
  onComfirm?: () => void;
  onCancel?: () => void;
  confirmLabel?: string;
  cancelLabel?: string;
  modalTitle?: string;
  modalDescription?: string;
}

export const WithModalConfirmation = ({
  buttonRender,
  onComfirm,
  onCancel,
  modalTitle,
  modalDescription,
  confirmLabel,
  cancelLabel,
}: WithModalConfirmationProps) => {
  const [isOpen, setIsOpen] = useState(false);

  const handleCancel = () => {
    setIsOpen(false);
  };

  const handleOpen = () => {
    setIsOpen(true);
  };

  return (
    <>
      {buttonRender({ openModal: handleOpen })}
      <ModalWindow
        isOpen={isOpen}
        setIsOpen={setIsOpen}
        onCancel={() => handleCancel()}
        onComfirm={() => onComfirm && onComfirm()}
        title={modalTitle}
        description={modalDescription}
        cancelLabel={cancelLabel}
        confirmLabel={confirmLabel}
      />
    </>
  );
};

export const ButtonWithComfirmationModal = ({
  titleModal,
  desc,
  onClick,
  onCancel,
  ...btnProps
}: ButtonWithComfirmationModalProps) => {
  const [isOpen, setIsOpen] = useState(false);

  const handleCancel = () => {
    setIsOpen(false);
    onCancel && onCancel();
  };

  return (
    <>
      <Button
        {...btnProps}
        onClick={() => {
          setIsOpen(true);
        }}
      />
      <ModalWindow
        isOpen={isOpen}
        setIsOpen={setIsOpen}
        onComfirm={() => onClick && onClick()}
        onCancel={() => handleCancel()}
        title={titleModal}
        description={desc}
      />
    </>
  );
};
